import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";

/* -------------------------------------------
   USE SAME API PATHS AS ISSUE TO LABOUR
-------------------------------------------- */
const pendingLabourInwardAPI = {
  // listing: api.warehouse.dispatch.issue_to_labour.listing,
  // getByID: api.warehouse.dispatch.issue_to_labour.getByID,
  listing: api.warehouse2.pendingLabourInward.list,
  getByID: api.warehouse2.pendingLabourInward.getByID,
  create: api.warehouse2.pendingLabourInward.create,
  update: api.warehouse2.pendingLabourInward.update,
  inventory_item_by_id: "/issue-to-labour/inventory-item/",
  export_csv: api.warehouse.dispatch.issue_to_labour.export_csv,
};

/* -------------------------------------------
   LIST PENDING LABOUR INWARD
-------------------------------------------- */
export const fetchPendingLabourInward = createAsyncThunk(
  "PendingLabourInward/fetchPendingLabourInward",
  async ({
    page,
    pageSize,
    search,
    sort,
    order,
    filters,
    warehouse_id,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    warehouse_id: string;
  }) => {
    const params = new URLSearchParams();

    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);

    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    const formatDate = (date: any) => {
      if (!date) return "";
      const d = new Date(date);
      return d.toISOString().split("T")[0];
    };

    if (filters?.length) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (!filterField || filterValue == null) return;

          if (typeof filterValue === "object") {
            const { from_date, to_date } = filterValue;

            if (
              from_date &&
              ["created_at", "updated_at", "dispatch_date"].includes(
                filterField
              )
            ) {
              params.append(`${filterField}_from`, formatDate(from_date));
            }
            if (
              to_date &&
              ["created_at", "updated_at", "dispatch_date"].includes(
                filterField
              )
            ) {
              params.append(`${filterField}_to`, formatDate(to_date));
            }

            return;
          }

          params.append(filterField, String(filterValue));
        }
      );
    }

    const response: any = await Axios.get(
      `${pendingLabourInwardAPI.listing}?warehouse_id=${warehouse_id}&dispatch_status=dispatched&${params.toString()}`
    );

    // console.log("List of pending inward",response)

    return {
      data: response?.data?.data || [],
      rowCount: response?.data?.count || 0,
    };
  }
);

/* -------------------------------------------
   GET BY ID
-------------------------------------------- */
export const fetchPendingLabourInwardById = createAsyncThunk(
  "PendingLabourInward/fetchPendingLabourInwardById",
  async (inward_id: string, { rejectWithValue }) => {
    try {
        // console.log("Response start")
      const response = await Axios.get(
        `${pendingLabourInwardAPI.getByID}${inward_id}`
      );
    //   console.log("Response from pending inward by id",response)
    //   console.log("Response start")

      return response?.data?.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to fetch labour inward details"
      );
    }
  }
);

/* -------------------------------------------
   CREATE LABOUR INWARD
-------------------------------------------- */
export const createPendingLabourInward = createAsyncThunk(
  "PendingLabourInward/createPendingLabourInward",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      // const response: any = await Axios.post(`warehouse/issue-to-labour-inward/create`,
      const response: any = await Axios.post(pendingLabourInwardAPI.create,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response?.data?.success) {
        return response.data.data;
      }

      return rejectWithValue(
        response?.data?.message ||
          response?.data?.error ||
          "Failed to create inward"
      );
    } catch (error: any) {
      const res = error?.response?.data || {};
      return rejectWithValue({
        message:
          res?.message ||
          res?.error ||
          error?.message ||
          "Failed to create inward",
        errors: res?.data?.errors || res?.errors,
      });
    }
  }
);

/* -------------------------------------------
   UPDATE LABOUR INWARD
-------------------------------------------- */

export const updatePendingLabourInward = createAsyncThunk(
  "PendingLabourInward/updatePendingLabourInward",
  async (
    { inventory_id, formData }: { inventory_id: string; formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.put(
        `${pendingLabourInwardAPI.update}${inventory_id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response?.data?.success) {
        return response.data.data;
      }

      return rejectWithValue(
        response?.data?.message ||
          response?.data?.error ||
          "Failed to update inward"
      );
    } catch (error: any) {
      const res = error?.response?.data || {};
      return rejectWithValue({
        message:
          res?.message ||
          res?.error ||
          error?.message ||
          "Failed to update inward",
        errors: res?.data?.errors || res?.errors,
      });
    }
  }
);

/* -------------------------------------------
   Revert LABOUR INWARD
-------------------------------------------- */
export const revertLabourInward = createAsyncThunk(
  "grn/revertGRN",
  async (inventory_id: string, { rejectWithValue }) => {
    try {
      const res: any = await Axios.delete(
        `${api.warehouse2.pendingLabourInward.revert}${inventory_id}`
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to revert GRN");
    }
  }
);

/* -------------------------------------------
   GET INVENTORY ITEM BY ID (same API as issue to labour)
-------------------------------------------- */
export const getInventoryItemByInventoryItemIdPendingInward = createAsyncThunk(
  "PendingLabourInward/getInventoryItemByInventoryItemIdPendingInward",
  async (
    {
      inventory_item_id,
      warehouse_id,
    }: {
      inventory_item_id: string;
      warehouse_id?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      if (warehouse_id) params.append("warehouse_id", warehouse_id);

      const url = `${pendingLabourInwardAPI.inventory_item_by_id}${inventory_item_id}${
        params.toString() ? `?${params.toString()}` : ""
      }`;

      const response: any = await Axios.get(url);

      if (response?.data?.success && response?.data?.data) {
        return response.data.data;
      }

      return rejectWithValue(
        response?.data?.message ||
          response?.data?.error ||
          "Failed to fetch inventory item"
      );
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to fetch inventory item"
      );
    }
  }
);

/* -------------------------------------------
   CSV EXPORT
-------------------------------------------- */
interface PendingInwardFilters {
  search?: string;
  warehouse_id?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: "asc" | "desc";
  [key: string]: any;
}

export const exportPendingLabourCSV = async (paramsObj: any = {}) => {
  const { filters = [], ...rest } = paramsObj;

  const params = new URLSearchParams();

  // Default params
  params.append("export", "csv");
  params.append("dispatch_status", "Dispatched");

  // Append primitive params like search, sort, sortOrder, warehouse_id
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  });

  // Handle filters array
  if (Array.isArray(filters)) {
    filters.forEach((filter: any) => {
      const filterField = filter.filterField || filter.id;
      const filterValue = filter.filterValue ?? filter.value;

      if (
        filterField &&
        filterValue !== undefined &&
        filterValue !== null &&
        filterValue !== ""
      ) {
        if (typeof filterValue === "object") {
          Object.entries(filterValue).forEach(([k, v]) => {
            if (v !== "" && v !== null && v !== undefined) {
              params.append(k, String(v));
            }
          });
        } else {
          params.append(filterField, String(filterValue));
        }
      }
    });
  }

  const response = await Axios.get(
    `${api.warehouse.dispatch.issue_to_labour.listing}?${params.toString()}`,
    { responseType: "blob" }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "issueToLabour.csv",
  };
};